import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Bot,
  CalendarDays,
  Clock,
  Loader2,
  MapPin,
  Package,
  Phone,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Truck,
  User,
  X,
} from 'lucide-react'
import { format, formatDistanceToNow, isToday, isTomorrow, isYesterday, parseISO } from 'date-fns'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { useBodyScrollLock } from '@/components/ui/Modal'
import { Card } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { db } from '@/lib/db/database'
import { strapiGet } from '@/lib/strapi'
import { todayISO } from '@/lib/utils/date'
import { useAppStore } from '@/lib/store/useAppStore'
import { useMyRestaurant } from '@/lib/queries/useRestaurants'
import { useSheAnswersConversation, useSheAnswersConversations } from '@/lib/queries/useSheAnswers'
import type { BookingPreOrderItem } from '@/lib/types/pos'
import type { CustomerOrder } from '@/lib/types/customerOrder'

type OrderSourceFilter = 'all' | 'she_answers' | 'admin_app' | 'manual'

const SOURCE_OPTIONS: Array<{ value: OrderSourceFilter; label: string }> = [
  { value: 'all', label: 'All sources' },
  { value: 'she_answers', label: 'SheAnswers AI' },
  { value: 'admin_app', label: 'Admin app' },
  { value: 'manual', label: 'Manual' },
]

const STATUS_LABELS: Record<string, string> = {
  request: 'New',
  approved: 'Preparing',
  arrived: 'Ready',
  in_delivery: 'In delivery',
  left: 'Completed',
  no_show: 'No show',
  canceled: 'Canceled',
}

const STATUS_STYLES: Record<string, string> = {
  request: 'border-blue-500/30 bg-blue-500/10 text-blue-500',
  approved: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
  arrived: 'border-green-500/30 bg-green-500/10 text-green-500',
  in_delivery: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-500',
  left: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-500',
  no_show: 'border-red-500/30 bg-red-500/10 text-red-500',
  canceled: 'border-stone-500/30 bg-stone-500/10 text-stone-500',
}

function dateLabel(date: string) {
  const parsed = parseISO(date)
  if (isToday(parsed)) return 'Today'
  if (isTomorrow(parsed)) return 'Tomorrow'
  if (isYesterday(parsed)) return 'Yesterday'
  return format(parsed, 'EEE, MMM d')
}

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount)
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`
  }
}

function sourceLabel(source: string | undefined) {
  if (source === 'she_answers') return 'SheAnswers AI'
  if (source === 'admin_app') return 'Admin app'
  if (source === 'website') return 'Customer page'
  if (source === 'walkin') return 'Walk-in'
  if (source === 'phone') return 'Phone'
  if (source === 'collection') return 'Collection'
  if (!source) return 'Manual'
  return source.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function sourceBadge(source: string | undefined) {
  const style =
    source === 'she_answers'
      ? 'border-purple-500/40 bg-purple-500/15 text-purple-500'
      : source === 'admin_app'
        ? 'border-blue-500/40 bg-blue-500/15 text-blue-500'
        : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-600'

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold uppercase ${style}`}>
      {sourceLabel(source)}
    </span>
  )
}

function matchesSource(order: CustomerOrder, filter: OrderSourceFilter) {
  if (filter === 'all') return true
  if (filter === 'manual') return order.source !== 'she_answers' && order.source !== 'admin_app'
  return order.source === filter
}

function selectedOptionTotal(selectedOptions: unknown) {
  if (!Array.isArray(selectedOptions)) return 0
  return selectedOptions.reduce((sum, option: any) => {
    const quantity = Math.max(1, Number(option?.quantity) || 1)
    const priceDelta = Number(option?.priceDelta ?? 0)
    return sum + (Number.isFinite(priceDelta) ? priceDelta * quantity : 0)
  }, 0)
}

function orderItemTotal(item: BookingPreOrderItem) {
  const unit = Number(item.menuItem?.basePrice ?? 0) + Number(item.variant?.priceDelta ?? 0)
  return item.menuItem?.soldByWeight === true && item.weightGrams != null
    ? unit * Number(item.weightGrams) / 1000 + selectedOptionTotal(item.selectedOptions)
    : (unit + selectedOptionTotal(item.selectedOptions)) * item.quantity
}

function formatWeight(grams: number) {
  return grams % 1000 === 0 ? `${grams / 1000} kg` : `${grams} g`
}

function orderTotal(order: CustomerOrder, items: BookingPreOrderItem[]) {
  const itemsTotal = items.reduce((sum, item) => sum + orderItemTotal(item), 0)
  return itemsTotal + Number(order.deliveryFee ?? 0)
}

function itemCount(items: BookingPreOrderItem[]) {
  return items.reduce((sum, item) => sum + item.quantity, 0)
}

function metadataConversationId(metadata: CustomerOrder['metadata']) {
  if (!metadata) return null
  const candidates = [
    metadata.conversationId,
    metadata.conversation_id,
    metadata.elevenlabsConversationId,
    metadata.elevenLabsConversationId,
  ]
  return candidates.find((value) => typeof value === 'string' && value.trim()) as string | null
}

function statusBadge(status: string) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] ?? STATUS_STYLES.request}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

function ConversationDrawer({
  conversationId,
  order,
  onClose,
}: {
  conversationId: string | null
  order: CustomerOrder | null
  onClose: () => void
}) {
  useBodyScrollLock(!!conversationId && !!order)
  const { data: detail, isLoading, isError } = useSheAnswersConversation(conversationId)
  const successful =
    detail?.call_successful === 'success' ||
    detail?.call_successful === 'true'

  return (
    <AnimatePresence>
      {conversationId && order && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[900] bg-black/60"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed inset-0 z-[901] flex w-full max-w-md flex-col bg-[var(--color-bg-surface)] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:border-l sm:border-[var(--color-border-subtle)] sm:pt-0 sm:pb-0"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border-subtle)] px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
                  {order.guest?.name ?? 'Guest'}
                </p>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
                  <span>{order.date} at {order.time}</span>
                  {detail && (
                    <>
                      <span>|</span>
                      <span>{Math.round(detail.call_duration_secs ?? 0)}s</span>
                      <span className={successful ? 'text-green-500' : 'text-red-500'}>
                        {successful ? 'Successful' : 'Unsuccessful'}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close conversation"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
              </div>
            ) : isError ? (
              <div className="p-4 text-sm text-[var(--color-text-muted)]">
                Conversation could not be loaded.
              </div>
            ) : (
              <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain p-4">
                <div className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                    Order created by SheAnswers
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                    {order.fulfillmentMethod === 'delivery' ? 'Delivery' : 'Pickup'} order
                    {order.internalId ? ` #${order.internalId}` : ''}
                  </p>
                </div>

                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                  Conversation
                </p>
                {detail?.transcript?.length ? (
                  <div className="space-y-3">
                    {detail.transcript.map((msg, index) => (
                      <div key={`${msg.time_in_call_secs}-${index}`} className={`flex gap-2 ${msg.role === 'agent' ? '' : 'flex-row-reverse'}`}>
                        <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${msg.role === 'agent' ? 'bg-purple-500/15' : 'bg-[var(--color-bg-elevated)]'}`}>
                          {msg.role === 'agent' ? (
                            <Bot className="h-3.5 w-3.5 text-purple-500" />
                          ) : (
                            <User className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                          )}
                        </div>
                        <div className={`max-w-[80%] rounded-[var(--radius-md)] px-3 py-2 text-sm text-[var(--color-text-primary)] ${msg.role === 'agent' ? 'bg-purple-500/10' : 'bg-[var(--color-bg-elevated)]'}`}>
                          {msg.message}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--color-text-muted)]">No transcript available.</p>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

function OrderRow({
  order,
  items,
  currency,
  conversationId,
  onOpenConversation,
}: {
  order: CustomerOrder
  items: BookingPreOrderItem[]
  currency: string
  conversationId: string | null
  onOpenConversation: (order: CustomerOrder, conversationId: string) => void
}) {
  const total = orderTotal(order, items)
  const count = itemCount(items)

  return (
    <Card className="overflow-hidden shadow-sm">
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(12rem,0.8fr)_auto] lg:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {order.guest?.name ?? 'Guest'}
            </p>
            {sourceBadge(order.source)}
            {statusBadge(order.status)}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-[var(--color-text-muted)]">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {order.asap ? 'ASAP' : order.time}
            </span>
            <span className="inline-flex items-center gap-1">
              {order.fulfillmentMethod === 'delivery' ? <Truck className="h-3.5 w-3.5" /> : <Package className="h-3.5 w-3.5" />}
              {order.fulfillmentMethod === 'delivery' ? 'Delivery' : 'Pickup'}
            </span>
            {order.guest?.phone && (
              <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-accent-amber)]/40 bg-[var(--color-accent-amber-dim)] px-2.5 py-1 text-sm font-bold text-[var(--color-text-primary)]">
                <Phone className="h-4 w-4 text-[var(--color-accent-amber)]" />
                {order.guest.phone}
              </span>
            )}
          </div>
          {order.deliveryAddress && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-[var(--color-text-muted)]">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-2">{order.deliveryAddress}</span>
            </div>
          )}
        </div>

        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {formatMoney(total, currency)}
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {count} item{count === 1 ? '' : 's'}
          </p>
          {order.createdAt && (
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              Created {formatDistanceToNow(parseISO(order.createdAt), { addSuffix: true })}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {order.source === 'she_answers' && conversationId ? (
            <Button size="sm" variant="secondary" onClick={() => onOpenConversation(order, conversationId)}>
              <Bot className="h-3.5 w-3.5" />
              Conversation
            </Button>
          ) : order.source === 'she_answers' ? (
            <span className="text-xs text-[var(--color-text-muted)]">Conversation pending</span>
          ) : null}
        </div>
      </div>

      {items.length > 0 && (
        <div className="border-t border-[var(--color-border-subtle)] px-4 py-3">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {items.slice(0, 6).map((item) => (
              <div key={item.documentId} className="flex items-center justify-between gap-3 rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs">
                <span className="min-w-0 truncate text-[var(--color-text-secondary)]">
                  <span className="font-semibold text-[var(--color-text-primary)]">{item.weightGrams != null ? formatWeight(item.weightGrams) : `${item.quantity}x`}</span>
                  {' '}
                  {item.menuItem?.name ?? 'Item'}
                </span>
                <span className="shrink-0 text-[var(--color-text-muted)]">
                  {formatMoney(orderItemTotal(item), currency)}
                </span>
              </div>
            ))}
            {items.length > 6 && (
              <div className="rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
                +{items.length - 6} more
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

export default function OrdersPage() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  const featureSheAnswers = useAppStore((s) => s.featureSheAnswers)
  const [selectedDate, setSelectedDate] = useState(todayISO())
  const [followsToday, setFollowsToday] = useState(true)
  const [sourceFilter, setSourceFilter] = useState<OrderSourceFilter>('all')
  const [search, setSearch] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [openConversation, setOpenConversation] = useState<{ order: CustomerOrder; conversationId: string } | null>(null)
  const { data: restaurantResp } = useMyRestaurant()

  const restaurant = Array.isArray((restaurantResp as any)?.data) ? (restaurantResp as any).data[0] : null
  const currency = restaurant?.currency ?? 'EUR'

  const dateOrders = useLiveQuery(
    () => db.customerOrders.where('date').equals(selectedDate).toArray(),
    [selectedDate],
  )
  const allPreOrderItems = useLiveQuery(() => db.preOrderItems.toArray(), [])

  const hasSheAnswersOrders = (dateOrders ?? []).some((order) => order.source === 'she_answers')
  const conversationsQuery = useSheAnswersConversations(featureSheAnswers && hasSheAnswersOrders, selectedDate)
  const conversations = conversationsQuery.data?.conversations ?? []

  useEffect(() => {
    if (!followsToday) return
    const interval = window.setInterval(() => {
      setSelectedDate(todayISO())
    }, 60_000)
    return () => window.clearInterval(interval)
  }, [followsToday])

  useEffect(() => {
    if (!restaurantId) return
    let cancelled = false
    let intervalId: number | null = null

    const syncOrders = async () => {
      setRefreshing(true)
      try {
        const resp = await strapiGet<CustomerOrder[]>('/api/customer-orders', {
          filters: {
            restaurant: { documentId: { $eq: restaurantId } },
            date: { $eq: selectedDate },
          },
          populate: {
            guest: true,
            openingHour: true,
            preOrderItems: { populate: { menuItem: { populate: { category: true, variants: true } }, variant: true } },
          },
          pagination: { pageSize: 500 },
          sort: ['dateTime:asc'],
        })
        if (cancelled) return
        const orders = ((resp as any).data ?? resp) as CustomerOrder[]
        const serverOrderIds = new Set(orders.map((order) => order.documentId))
        const staleOrderKeys = await db.customerOrders
          .filter((order) =>
            order.date === selectedDate &&
            !(order.documentId ?? '').startsWith('temp-') &&
            !serverOrderIds.has(order.documentId),
          )
          .primaryKeys()
        if (staleOrderKeys.length > 0) await db.customerOrders.bulkDelete(staleOrderKeys as string[])
        await db.customerOrders.bulkPut(orders)
        const items = orders.flatMap((order) =>
          (order.preOrderItems ?? []).map((item) => ({
            ...item,
            orderDocumentId: order.documentId,
          })),
        )
        if (items.length > 0) await db.preOrderItems.bulkPut(items as BookingPreOrderItem[])
      } finally {
        if (!cancelled) setRefreshing(false)
      }
    }

    void syncOrders()
    intervalId = window.setInterval(() => {
      void syncOrders()
    }, 30_000)

    return () => {
      cancelled = true
      if (intervalId != null) window.clearInterval(intervalId)
    }
  }, [restaurantId, selectedDate])

  const itemsByOrder = useMemo(() => {
    const map = new Map<string, BookingPreOrderItem[]>()
    for (const item of allPreOrderItems ?? []) {
      const key = item.orderDocumentId ?? ''
      if (!key) continue
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(item)
    }
    return map
  }, [allPreOrderItems])

  const conversationByOrderId = useMemo(() => {
    const map = new Map<string, string>()
    for (const conversation of conversations) {
      if (conversation.linkedOrder?.documentId) {
        map.set(conversation.linkedOrder.documentId, conversation.conversation_id)
      }
    }
    return map
  }, [conversations])

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (dateOrders ?? [])
      .filter((order) => matchesSource(order, sourceFilter))
      .filter((order) => {
        if (!q) return true
        return (
          order.guest?.name?.toLowerCase().includes(q) ||
          order.guest?.phone?.toLowerCase().includes(q) ||
          order.guest?.email?.toLowerCase().includes(q) ||
          order.internalId?.toLowerCase().includes(q)
        )
      })
      .sort((a, b) => {
        const aTime = a.dateTime || `${a.date}T${a.time}`
        const bTime = b.dateTime || `${b.date}T${b.time}`
        return aTime.localeCompare(bTime)
      })
  }, [dateOrders, search, sourceFilter])

  const totals = useMemo(() => {
    const orders = dateOrders ?? []
    return {
      all: orders.length,
      sheAnswers: orders.filter((order) => order.source === 'she_answers').length,
      admin: orders.filter((order) => order.source === 'admin_app').length,
      manual: orders.filter((order) => order.source !== 'she_answers' && order.source !== 'admin_app').length,
    }
  }, [dateOrders])

  const handleDateChange = (value: string) => {
    setSelectedDate(value)
    setFollowsToday(value === todayISO())
  }

  const handleToday = () => {
    setSelectedDate(todayISO())
    setFollowsToday(true)
  }

  const isLoading = dateOrders === undefined

  return (
    <div className="min-w-0">
      <PageHeader
        title="Orders"
        description={`${dateLabel(selectedDate)} order flow across SheAnswers, admin app, and manual channels.`}
        actions={
          <Button variant="secondary" size="sm" onClick={handleToday}>
            <CalendarDays className="h-4 w-4" />
            Today
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 lg:grid-cols-[minmax(10rem,12rem)_minmax(12rem,14rem)_minmax(0,1fr)_auto]">
        <div className="relative">
          <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => handleDateChange(event.target.value)}
            className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 pl-9 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-accent-amber)]/50 focus:outline-none"
          />
        </div>
        <Select
          aria-label="Source"
          value={sourceFilter}
          onChange={(event) => setSourceFilter(event.target.value as OrderSourceFilter)}
          options={SOURCE_OPTIONS}
        />
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, phone, email, reference..."
            className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 pl-9 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-amber)]/50 focus:outline-none"
          />
        </div>
        <div className="flex h-10 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 text-xs text-[var(--color-text-muted)]">
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Live
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ['All', totals.all],
          ['SheAnswers AI', totals.sheAnswers],
          ['Admin app', totals.admin],
          ['Manual', totals.manual],
        ].map(([label, value]) => (
          <Card key={label} className="p-4 shadow-sm">
            <p className="text-xs font-medium text-[var(--color-text-muted)]">{label}</p>
            <p className="mt-1 text-2xl font-bold text-[var(--color-text-primary)]">{value}</p>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-sm text-[var(--color-text-muted)]">
          Loading orders...
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border-subtle)] py-20 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)]">
            <SlidersHorizontal className="h-7 w-7 text-[var(--color-text-muted)]" />
          </div>
          <p className="font-semibold text-[var(--color-text-primary)]">No orders found</p>
          <p className="mt-1 max-w-xs text-sm text-[var(--color-text-muted)]">
            Change the date, source, or search to see more orders.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const items = itemsByOrder.get(order.documentId) ?? order.preOrderItems ?? []
            const conversationId = metadataConversationId(order.metadata) ?? conversationByOrderId.get(order.documentId) ?? null
            return (
              <OrderRow
                key={order.documentId}
                order={order}
                items={items}
                currency={currency}
                conversationId={conversationId}
                onOpenConversation={(nextOrder, nextConversationId) =>
                  setOpenConversation({ order: nextOrder, conversationId: nextConversationId })
                }
              />
            )
          })}
        </div>
      )}

      <ConversationDrawer
        conversationId={openConversation?.conversationId ?? null}
        order={openConversation?.order ?? null}
        onClose={() => setOpenConversation(null)}
      />
    </div>
  )
}
